import { useCurrentUserCtx } from 'client/contexts/CurrentUser';
import { localStorage } from 'client/modules/Browser/localStorage';
import { IS_EXISTING_USER_KEY } from 'client/screens/NewEpisodeRedirectScreen';
import { useEffect, useState } from 'react';

/**
 * this hook allows us to dynamically generate URL paths we'd want to direct
 * users with the given destination path.
 *
 * 0. default the path to the given destination
 * 1. if the user is logged in already, just return the default destination path
 * 2. if the user is not logged in and hasn't signed up before in this browser,
 *    send them to the sign up page
 * 3. if the user is not logged in and has signed up before, send them to the
 *    login page with `return_to` parameter set that will redirect them on login
 */
export function useDynamicUrlPath({
  destinationPath,
}: {
  destinationPath: string;
}) {
  const {
    state: { userId },
  } = useCurrentUserCtx();
  const [path, setPath] = useState(destinationPath);
  const [isExistingUser, setIsExistingUser] = useState(false);

  /**
   * if user is not logged in, check indexeddb value to see if they haved logged
   * into an anchor account from this browser in the past and set it into state
   */
  useEffect(() => {
    localStorage
      .getItem(IS_EXISTING_USER_KEY)
      .then(isExistingUserFromIndexedDb => {
        setIsExistingUser(isExistingUserFromIndexedDb);
      });
  }, []);

  /**
   * set the dynamic destination path based on if the user has logged in to
   * anchor from this browser in the past
   */
  useEffect(() => {
    let dynamicDestinationPath = destinationPath;
    if (!userId) {
      dynamicDestinationPath = isExistingUser
        ? `/login?return_to=${encodeURIComponent(destinationPath)}`
        : '/signup';
    }
    setPath(dynamicDestinationPath);
  }, [isExistingUser, destinationPath, userId]);

  return { path };
}
